import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Printer, X, FileText } from 'lucide-react';
import axiosInstance from '@/utils/axios';
import { toast } from 'sonner';
import { printPrescriptions } from '@/services/printService';

export default function BatchPrintModal({ open, onClose, selectedPatients }) {
  const [patientsWithPrescriptions, setPatientsWithPrescriptions] = useState([]);
  const [selectedPrescriptions, setSelectedPrescriptions] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (open && selectedPatients.length > 0) {
      fetchAllPrescriptions();
    }
  }, [open, selectedPatients]);

  const fetchAllPrescriptions = async () => {
    setLoading(true);
    try {
      const promises = selectedPatients.map(async (patient) => {
        try {
          // Buscar TODAS as receitas do paciente
          const response = await axiosInstance.get(`/prescriptions/patient/${patient.id}/all`);
          return {
            patient,
            prescriptions: response.data || []
          };
        } catch (error) {
          console.error(`Erro ao buscar receitas de ${patient.nome}:`, error);
          // Tentar buscar apenas uma receita (fallback)
          try {
            const singleResponse = await axiosInstance.get(`/prescriptions/patient/${patient.id}`);
            return {
              patient,
              prescriptions: [singleResponse.data]
            };
          } catch (fallbackError) {
            return {
              patient,
              prescriptions: []
            };
          }
        }
      });

      const results = await Promise.all(promises);
      setPatientsWithPrescriptions(results);
      
      // Auto-selecionar receitas
      const initialSelection = {};
      results.forEach(({ patient, prescriptions }) => {
        if (prescriptions.length > 0) {
          // Se o paciente tem _selectedPrescriptionId, selecionar apenas essa receita
          if (patient._selectedPrescriptionId) {
            const targetPrescription = prescriptions.find(p => p.id === patient._selectedPrescriptionId);
            if (targetPrescription) {
              initialSelection[patient.id] = [targetPrescription.id];
            } else {
              // Fallback: selecionar todas se não encontrar a específica
              initialSelection[patient.id] = prescriptions.map(p => p.id);
            }
          } else {
            // Comportamento padrão: selecionar todas as receitas
            initialSelection[patient.id] = prescriptions.map(p => p.id);
          }
        }
      });
      setSelectedPrescriptions(initialSelection);
      
      const totalPrescriptions = results.reduce((acc, r) => acc + r.prescriptions.length, 0);
      if (totalPrescriptions === 0) {
        toast.warning('Nenhuma receita encontrada para os pacientes selecionados');
      }
    } catch (error) {
      console.error('Erro ao buscar receitas:', error);
      toast.error('Erro ao carregar receitas');
    } finally {
      setLoading(false);
    }
  };

  const togglePrescription = (patientId, prescriptionId) => {
    setSelectedPrescriptions(prev => {
      const patientPrescriptions = prev[patientId] || [];
      if (patientPrescriptions.includes(prescriptionId)) {
        // Remove
        return {
          ...prev,
          [patientId]: patientPrescriptions.filter(id => id !== prescriptionId)
        };
      } else {
        // Add
        return {
          ...prev,
          [patientId]: [...patientPrescriptions, prescriptionId]
        };
      }
    });
  };

  const toggleAllPatientPrescriptions = (patientId, prescriptions) => {
    setSelectedPrescriptions(prev => {
      const patientPrescriptions = prev[patientId] || [];
      if (patientPrescriptions.length === prescriptions.length) {
        // Desmarcar todas
        return {
          ...prev,
          [patientId]: []
        };
      } else {
        // Marcar todas
        return {
          ...prev,
          [patientId]: prescriptions.map(p => p.id)
        };
      }
    });
  };

  const getTotalSelectedPrescriptions = () => {
    return Object.values(selectedPrescriptions).reduce((acc, arr) => acc + arr.length, 0);
  };

  const getSelectedPrescriptionsForPrint = () => {
    const result = [];
    patientsWithPrescriptions.forEach(({ patient, prescriptions }) => {
      const selectedIds = selectedPrescriptions[patient.id] || [];
      prescriptions.forEach(prescription => {
        if (selectedIds.includes(prescription.id)) {
          result.push({ patient, prescription });
        }
      });
    });
    return result;
  };

  // Função para registrar impressão e atualizar datas
  const registerPrint = async (prescriptionIds) => {
    try {
      const promises = prescriptionIds.map(id => 
        axiosInstance.post(`/prescriptions/${id}/print`)
      );
      await Promise.all(promises);
      return true;
    } catch (error) {
      console.error('Erro ao registrar impressão:', error);
      return false;
    }
  };

  const handlePrint = async () => {
    const total = getTotalSelectedPrescriptions();
    if (total === 0) {
      toast.error('Selecione pelo menos uma receita para imprimir');
      return;
    }
    
    // Coletar IDs das receitas selecionadas
    const selectedIds = [];
    Object.entries(selectedPrescriptions).forEach(([patientId, prescriptionIds]) => {
      prescriptionIds.forEach(id => selectedIds.push(id));
    });
    
    // Registrar impressão de todas as receitas
    await registerPrint(selectedIds);
    
    // Recarregar dados para ter as datas atualizadas
    await fetchAllPrescriptions();
    
    // Preparar itens e imprimir via nova janela
    const items = getSelectedPrescriptionsForPrint();
    printPrescriptions(items);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">
              Imprimir Receitas em Lote
            </DialogTitle>
            <DialogDescription className="flex items-center justify-between">
              <span>{selectedPatients.length} paciente(s) selecionado(s)</span>
              <Badge variant="outline" className="ml-4">
                {getTotalSelectedPrescriptions()} receita(s) para imprimir
              </Badge>
            </DialogDescription>
          </DialogHeader>

          {loading ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground">Carregando receitas...</p>
            </div>
          ) : (
            <>
              <div className="flex gap-2 mb-4 no-print">
                <Button
                  data-testid="batch-print-button"
                  onClick={handlePrint}
                  disabled={getTotalSelectedPrescriptions() === 0}
                  className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-4"
                >
                  <Printer className="w-4 h-4 mr-2" />
                  Imprimir Selecionadas ({getTotalSelectedPrescriptions()})
                </Button>
              </div>

              <div className="space-y-4 no-print max-h-[500px] overflow-y-auto">
                {patientsWithPrescriptions.map(({ patient, prescriptions }) => (
                  <div key={patient.id} className="border rounded-lg p-4 bg-gray-50">
                    {/* Patient Header */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <span className="font-bold text-primary">{patient.nome.charAt(0)}</span>
                        </div>
                        <div>
                          <h3 className="font-bold text-lg">{patient.nome}</h3>
                          <p className="text-sm text-muted-foreground">
                            {prescriptions.length} receita(s) cadastrada(s)
                          </p>
                        </div>
                      </div>
                      {prescriptions.length > 1 && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => toggleAllPatientPrescriptions(patient.id, prescriptions)}
                          className="rounded-full"
                        >
                          {(selectedPrescriptions[patient.id] || []).length === prescriptions.length 
                            ? 'Desmarcar Todas' 
                            : 'Marcar Todas'
                          }
                        </Button>
                      )}
                    </div>

                    {/* Prescriptions List */}
                    <div className="space-y-2 ml-13">
                      {prescriptions.length === 0 ? (
                        <p className="text-sm text-muted-foreground italic">
                          Nenhuma receita encontrada
                        </p>
                      ) : (
                        prescriptions.map((prescription, index) => {
                          const isSelected = (selectedPrescriptions[patient.id] || []).includes(prescription.id);
                          const prescriptionName = prescription.name && prescription.name.trim() 
                            ? prescription.name 
                            : `Receita ${index + 1}`;
                          return (
                            <div 
                              key={prescription.id}
                              className={`flex items-center gap-3 p-3 rounded-lg border transition-colors cursor-pointer ${
                                isSelected ? 'bg-primary/5 border-primary' : 'bg-white border-gray-200 hover:border-gray-300'
                              }`}
                              onClick={() => togglePrescription(patient.id, prescription.id)}
                            >
                              <Checkbox
                                checked={isSelected}
                                onCheckedChange={() => togglePrescription(patient.id, prescription.id)}
                                className="pointer-events-none"
                              />
                              <FileText className="w-4 h-4 text-muted-foreground" />
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-sm">{prescriptionName}</span>
                                  <Badge variant="secondary" className="text-xs">
                                    {prescription.uso}
                                  </Badge>
                                </div>
                                <p className="text-xs text-muted-foreground mt-1">
                                  {prescription.medicamentos.length} medicamento(s) • 
                                  Validade: {new Date(prescription.data_validade).toLocaleDateString('pt-BR')}
                                </p>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
